import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Phone,
  Mail,
  Globe,
  Check,
  Award,
  BookOpen,
  Users,
  MessageSquare,
} from "lucide-react";

/**
 * 3-page A4 course brochure — config-driven.
 * Page 1: Cover · Page 2: Curriculum + Agenda · Page 3: Investment + About + CTA
 * Print rules:
 *   - Each page is exactly 210mm × 297mm with overflow-hidden.
 *   - Pages 1 & 2 use `.print:break-after-page` for a clean pagination break.
 */
export default function CourseBrochure({ course }) {
  return (
    <main
      data-testid={`brochure-${course.slug}`}
      className="min-h-screen bg-sebyx-bg text-sebyx-text font-body"
    >
      <header className="print:hidden sticky top-0 z-50 bg-sebyx-bg/95 backdrop-blur border-b border-sebyx-border">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <ArrowLeft
              size={16}
              className="text-sebyx-accent group-hover:-translate-x-0.5 transition-transform"
            />
            <span className="block w-2 h-2 bg-sebyx-accent" />
            <span className="font-display text-xl font-semibold tracking-tight">
              Sebyx
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted">
              {course.name} · Brochure · 3 pages
            </span>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="brochure-print-btn"
              className="px-5 py-2 bg-sebyx-accent text-sebyx-bg font-semibold text-sm hover:bg-[#3FA097] transition-colors"
            >
              Print / Save as PDF
            </button>
          </div>
        </div>
      </header>

      <div className="print:hidden max-w-5xl mx-auto px-6 md:px-10 pt-8 pb-4">
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-sebyx-muted leading-relaxed">
          Tip · Print dialog: <span className="text-sebyx-accent">Layout = Portrait</span>,
          {" "}Margins = <span className="text-sebyx-accent">None</span>, Scale = 100%, Background graphics = <span className="text-sebyx-accent">On</span>, Headers/footers = <span className="text-sebyx-accent">Off</span>. Three A4 pages.
        </p>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-10 py-6 print:p-0 print:max-w-none flex flex-col items-center gap-8 print:gap-0">
        <CoverPage course={course} />
        <CurriculumPage course={course} />
        <InvestmentPage course={course} />
      </div>
    </main>
  );
}

/* ------------------------------- PAGE 1 · COVER ------------------------------ */

function CoverPage({ course }) {
  return (
    <section
      data-testid="brochure-page-1"
      className="relative bg-sebyx-bg border border-sebyx-border sebyx-corner overflow-hidden print:border-0 print:break-after-page"
      style={{
        width: "210mm",
        height: "297mm",
        margin: "0 auto",
        padding: "14mm 16mm",
      }}
    >
      <div className="absolute inset-0 sebyx-grid-bg opacity-30 pointer-events-none" />
      {/* accent corner slash */}
      <div className="absolute -top-24 -right-24 w-96 h-96 rotate-45 bg-sebyx-accent/[0.04] pointer-events-none" />
      <div className="relative h-full flex flex-col">
        <BrandBar tagline="Course Brochure · 2026" pageMark="01 / 03" />

        {/* Hero */}
        <div className="mt-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="block w-10 h-px bg-sebyx-accent" />
            <span className="font-mono text-[10px] tracking-[0.35em] uppercase text-sebyx-accent">
              {course.eyebrow}
            </span>
          </div>
          <h1 className="font-display text-[52px] font-semibold tracking-tighter leading-[0.95] text-sebyx-text">
            {course.coverTitle.pre}{" "}
            <span className="text-sebyx-accent italic font-light">
              {course.coverTitle.accent}
            </span>
            <br />
            {course.coverTitle.post}
          </h1>
          <p className="mt-6 text-[14px] leading-relaxed text-sebyx-muted max-w-2xl">
            {course.coverPromise}
          </p>
        </div>

        {/* 4-stat number strip */}
        <div className="mt-10 grid grid-cols-4 gap-px bg-sebyx-border border border-sebyx-border">
          {course.stats.map((s, i) => (
            <div key={i} className="bg-sebyx-bg px-4 py-5 flex flex-col">
              <span className="font-display text-2xl font-semibold text-sebyx-text leading-tight">
                {s.k}
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.28em] text-sebyx-muted mt-1">
                {s.v}
              </span>
            </div>
          ))}
        </div>

        {/* Who + What */}
        <div className="mt-8 grid grid-cols-12 gap-6">
          <div className="col-span-7">
            <span className="font-mono text-[10px] tracking-[0.35em] uppercase text-sebyx-accent">
              Who It's For
            </span>
            <ul className="mt-3 space-y-2">
              {course.whoItsFor.map((s, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-[12px] text-sebyx-text/90 leading-snug"
                >
                  <span className="block w-1.5 h-1.5 rotate-45 bg-sebyx-accent mt-1.5 shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="col-span-5 border-l-2 border-sebyx-accent pl-4 bg-sebyx-surface/40 py-3">
            <span className="font-mono text-[10px] tracking-[0.35em] uppercase text-sebyx-accent">
              What You Take Home
            </span>
            <ul className="mt-3 space-y-1.5 text-[11.5px] text-sebyx-text/90">
              {course.whatYouGet.map((s) => (
                <li key={s} className="flex items-start gap-2">
                  <Check
                    size={11}
                    strokeWidth={2.5}
                    className="text-sebyx-accent shrink-0 mt-1"
                  />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA panel */}
        <div className="mt-auto pt-6">
          <div className="border border-sebyx-accent bg-sebyx-surface/40 p-4 grid grid-cols-12 gap-3 items-center">
            <div className="col-span-7">
              <div className="flex items-center gap-2 mb-2">
                <span className="block w-1.5 h-1.5 rotate-45 bg-sebyx-accent" />
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-sebyx-accent">
                  Invitation-only · Apply for the Next Cohort
                </span>
              </div>
              <h3 className="font-display text-lg font-semibold tracking-tight text-sebyx-text leading-tight">
                Small-batch cohorts of 10–15 seats.
              </h3>
              <p className="text-[10.5px] text-sebyx-text/85 leading-snug mt-1">
                See pages 02–03 for the two-day teaching plan, the Sebyx method,
                and expected outcomes. Programme investment is shared on your
                qualification call.
              </p>
            </div>
            <div className="col-span-5 grid grid-cols-1 gap-1.5 text-[10px]">
              <Contact
                icon={Users}
                label={course.advisor.role}
                value={`${course.advisor.name} · ${course.advisor.phone}`}
              />
              <Contact icon={Mail} label="Email" value={course.advisor.email} />
              <Contact icon={Globe} label="Web" value="www.sebyx.com" />
            </div>
          </div>
          <FooterStrip label={`Sebyx Academy · ${course.name} · Brochure 01`} />
        </div>
      </div>
    </section>
  );
}

/* --------------------------- PAGE 2 · CURRICULUM ----------------------------- */

function CurriculumPage({ course }) {
  return (
    <section
      data-testid="brochure-page-2"
      className="relative bg-sebyx-bg border border-sebyx-border sebyx-corner overflow-hidden print:border-0 print:break-after-page"
      style={{
        width: "210mm",
        height: "297mm",
        margin: "0 auto",
        padding: "12mm 14mm",
      }}
    >
      <div className="absolute inset-0 sebyx-grid-bg opacity-30 pointer-events-none" />
      <div className="relative h-full flex flex-col">
        <BrandBar tagline={`${course.name} · Curriculum`} pageMark="02 / 03" />

        {/* Section title */}
        <div className="mt-4">
          <div className="flex items-center gap-3 mb-2">
            <span className="block w-7 h-px bg-sebyx-accent" />
            <span className="font-mono text-[9.5px] tracking-[0.35em] uppercase text-sebyx-accent">
              The 2-Day Immersive · 8 Modules · 60+ Scripts
            </span>
          </div>
          <h2 className="font-display text-[22px] font-semibold tracking-tighter leading-[1.1] text-sebyx-text">
            How the two days actually run.
          </h2>
        </div>

        {/* Day 1 · Day 2 agenda */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          {course.agenda.map((day, di) => (
            <div
              key={di}
              className="border border-sebyx-border bg-sebyx-surface/30 p-3"
            >
              <div className="flex items-center justify-between pb-2 border-b border-sebyx-border">
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-sebyx-accent">
                  Day {di + 1}
                </span>
                <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-sebyx-muted">
                  10 AM – 6 PM
                </span>
              </div>
              <ul className="mt-2 space-y-1">
                {day.map((slot, si) => (
                  <li
                    key={si}
                    className="grid grid-cols-12 gap-2 text-[10.5px] leading-snug"
                  >
                    <span className="col-span-4 font-mono text-[9px] tracking-[0.2em] text-sebyx-muted uppercase pt-0.5">
                      {slot.time}
                    </span>
                    <span className="col-span-8 text-sebyx-text/90">
                      {slot.title}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* 8-module grid */}
        <div className="mt-4">
          <div className="flex items-center gap-3 mb-2">
            <span className="block w-7 h-px bg-sebyx-accent" />
            <span className="font-mono text-[9.5px] tracking-[0.35em] uppercase text-sebyx-accent">
              Module Breakdown
            </span>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {course.modules.map((m) => {
              const Icon = m.Icon;
              return (
                <div
                  key={m.n}
                  className="border border-sebyx-border bg-sebyx-surface/30 p-2 flex flex-col"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-7 h-7 border border-sebyx-accent flex items-center justify-center shrink-0">
                      <Icon size={12} className="text-sebyx-accent" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[8.5px] tracking-[0.3em] uppercase text-sebyx-accent">
                          {m.n}
                        </span>
                        <span className="font-mono text-[7.5px] tracking-[0.2em] uppercase text-sebyx-muted">
                          · {m.hours}
                        </span>
                      </div>
                      <div className="font-display text-[11.5px] font-semibold text-sebyx-text leading-tight">
                        {m.title}
                      </div>
                    </div>
                  </div>
                  <ul className="pl-2 space-y-0.5">
                    {m.lines.map((l, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-1.5 text-[9px] text-sebyx-text/85 leading-snug"
                      >
                        <span className="block w-1 h-1 rotate-45 bg-sebyx-accent mt-1.5 shrink-0" />
                        {l}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        {/* Certification + Playbook */}
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="border border-sebyx-accent bg-sebyx-surface/40 p-3 flex gap-3 items-start">
            <div className="w-8 h-8 border border-sebyx-accent flex items-center justify-center shrink-0">
              <Award size={14} className="text-sebyx-accent" />
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-sebyx-accent">
                Certification
              </div>
              <div className="font-display text-[13px] font-semibold text-sebyx-text leading-tight mt-0.5">
                Sebyx {course.name} · Certified
              </div>
              <div className="text-[9.5px] text-sebyx-text/85 leading-snug mt-1">
                Printed certificate + LinkedIn credential. Awarded on completion
                of all 8 modules and the final live drill.
              </div>
            </div>
          </div>
          <div className="border border-sebyx-accent bg-sebyx-surface/40 p-3 flex gap-3 items-start">
            <div className="w-8 h-8 border border-sebyx-accent flex items-center justify-center shrink-0">
              <BookOpen size={14} className="text-sebyx-accent" />
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-sebyx-accent">
                The Playbook
              </div>
              <div className="font-display text-[13px] font-semibold text-sebyx-text leading-tight mt-0.5">
                40-page printed workbook
              </div>
              <div className="text-[9.5px] text-sebyx-text/85 leading-snug mt-1">
                Every script, framework, and drill in one binder. Yours to keep,
                reference, and hand to the next hire.
              </div>
            </div>
          </div>
        </div>

        <div className="mt-auto pt-3">
          <FooterStrip label={`Sebyx Academy · ${course.name} · Brochure 02`} />
        </div>
      </div>
    </section>
  );
}

/* ---------------------- PAGE 3 · INVESTMENT + ABOUT + CTA -------------------- */

function InvestmentPage({ course }) {
  return (
    <section
      data-testid="brochure-page-3"
      className="relative bg-sebyx-bg border border-sebyx-border sebyx-corner overflow-hidden print:border-0"
      style={{
        width: "210mm",
        height: "297mm",
        margin: "0 auto",
        padding: "12mm 14mm",
      }}
    >
      <div className="absolute inset-0 sebyx-grid-bg opacity-30 pointer-events-none" />
      <div className="relative h-full flex flex-col">
        <BrandBar tagline={`${course.name} · Method & Outcomes`} pageMark="03 / 03" />

        {/* Section title */}
        <div className="mt-4">
          <div className="flex items-center gap-3 mb-2">
            <span className="block w-7 h-px bg-sebyx-accent" />
            <span className="font-mono text-[9.5px] tracking-[0.35em] uppercase text-sebyx-accent">
              The Sebyx Method · Expected Outcomes
            </span>
          </div>
          <h2 className="font-display text-[22px] font-semibold tracking-tighter leading-[1.1] text-sebyx-text">
            Why this course actually moves the needle.
          </h2>
        </div>

        {/* Method + Outcomes */}
        <div className="mt-4 grid grid-cols-12 gap-3">
          {/* The Sebyx Method — 4 pillars */}
          <div className="col-span-7">
            <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-sebyx-accent">
              The Sebyx Method · 4 Pillars
            </span>
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              {METHOD_PILLARS.map((p, i) => {
                const Icon = p.Icon;
                return (
                  <div
                    key={i}
                    className="border border-sebyx-border bg-sebyx-surface/30 p-2.5"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 border border-sebyx-accent flex items-center justify-center shrink-0">
                        <Icon size={11} className="text-sebyx-accent" />
                      </div>
                      <span className="font-mono text-[8.5px] tracking-[0.3em] uppercase text-sebyx-accent">
                        0{i + 1}
                      </span>
                      <span className="font-display text-[11.5px] font-semibold text-sebyx-text leading-tight">
                        {p.title}
                      </span>
                    </div>
                    <p className="text-[9.5px] text-sebyx-text/85 leading-snug">
                      {p.detail}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Outcomes */}
          <div className="col-span-5 border-l-2 border-sebyx-accent bg-sebyx-surface/40 pl-3 py-2">
            <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-sebyx-accent">
              Outcomes You Can Expect
            </span>
            <ul className="mt-2 space-y-2">
              {course.outcomes.map((o, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check
                    size={11}
                    strokeWidth={2.5}
                    className="text-sebyx-accent shrink-0 mt-1"
                  />
                  <div>
                    <div className="font-display text-[11px] font-semibold text-sebyx-text leading-tight">
                      {o.title}
                    </div>
                    <div className="text-[9.5px] text-sebyx-text/85 leading-snug mt-0.5">
                      {o.detail}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Delivery note — invitation-only positioning */}
        <div className="mt-3 border-l-2 border-sebyx-accent bg-sebyx-surface/40 pl-3 py-2">
          <div className="flex items-center gap-2">
            <span className="block w-1.5 h-1.5 rotate-45 bg-sebyx-accent" />
            <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-sebyx-accent">
              Invitation-only · Small-batch Programme
            </span>
          </div>
          <p className="text-[10.5px] text-sebyx-text/90 leading-snug mt-1">
            Cohorts are capped at 10–15 seats and admitted by application.
            Delivered pan-India from Bangalore — online live, F2F weekend
            (Bangalore, Indore, Nagpur, Mumbai, Pune, Ahmedabad), 1-on-1
            personalised, or private in-company. Programme investment is shared
            on your qualification call.
          </p>
        </div>

        {/* About Sebyx / Mandar */}
        <div className="mt-3 grid grid-cols-12 gap-3">
          <div className="col-span-8">
            <div className="flex items-center gap-3 mb-2">
              <span className="block w-6 h-px bg-sebyx-accent" />
              <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-sebyx-accent">
                About Sebyx · About Mandar
              </span>
            </div>
            <p className="text-[10.5px] text-sebyx-text/90 leading-relaxed">
              Sebyx is a revenue architecture practice founded by{" "}
              <span className="text-sebyx-text font-medium">
                Mandar Prabhudesai
              </span>
              , a sales veteran with 18+ years of ground-up field experience
              across Med-Tech, Defence, Electronics, BFSI, and premium Retail.
              Sebyx Academy is the training arm — every module, script, and
              drill in {course.name} is drawn from live deals Mandar has
              personally closed or coached.
            </p>
            <p className="text-[10.5px] text-sebyx-text/85 leading-relaxed mt-2">
              Mandar has led national sales teams, architected go-to-market for
              multi-crore product launches, and mentored 200+ salespeople from
              first-job to team-lead. This course compresses that experience
              into two immersive days.
            </p>
          </div>
          <div className="col-span-4 border border-sebyx-border bg-sebyx-surface/40 p-3">
            <div className="font-mono text-[8.5px] tracking-[0.3em] uppercase text-sebyx-accent mb-2">
              Track Record
            </div>
            <ul className="space-y-2 text-[10px] text-sebyx-text/90">
              {[
                { k: "18+ yrs", v: "Field experience" },
                { k: "5 sectors", v: "High-barrier B2B & retail" },
                { k: "200+", v: "Salespeople trained" },
                { k: "Pan-India", v: "Reach · KA · MH · GJ hubs" },
              ].map((t, i) => (
                <li key={i}>
                  <div className="font-display text-[13px] font-semibold text-sebyx-text leading-tight">
                    {t.k}
                  </div>
                  <div className="font-mono text-[7.5px] tracking-[0.25em] uppercase text-sebyx-muted">
                    {t.v}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Next steps CTA */}
        <div className="mt-auto pt-3">
          <div className="border border-sebyx-accent bg-sebyx-surface/40 p-4">
            <div className="grid grid-cols-12 gap-3 items-center">
              <div className="col-span-7">
                <div className="flex items-center gap-2 mb-2">
                  <span className="block w-6 h-px bg-sebyx-accent" />
                  <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-sebyx-accent">
                    Next Steps · Application Process
                  </span>
                </div>
                <h3 className="font-display text-lg font-semibold tracking-tight text-sebyx-text leading-tight">
                  Apply for the next cohort.
                </h3>
                <ol className="mt-2 space-y-1 text-[10px] text-sebyx-text/90 leading-snug">
                  <li className="flex items-start gap-2">
                    <span className="font-mono text-sebyx-accent">01 —</span>
                    <span>Submit a short application via sebyx.com or WhatsApp.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-mono text-sebyx-accent">02 —</span>
                    <span>10-minute qualification call with Mandar to confirm fit.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-mono text-sebyx-accent">03 —</span>
                    <span>On acceptance, receive your cohort dates, brief, and calendar invites.</span>
                  </li>
                </ol>
              </div>
              <div className="col-span-5 grid grid-cols-1 gap-1.5">
                <Contact
                  icon={Users}
                  label={course.advisor.role}
                  value={course.advisor.name}
                />
                <Contact icon={Phone} label="Advisor · WhatsApp" value={course.advisor.phone} />
                <Contact icon={Mail} label="Email" value={course.advisor.email} />
                <Contact icon={Globe} label="Web" value="www.sebyx.com" />
              </div>
            </div>
          </div>
          <FooterStrip label={`Sebyx Academy · ${course.name} · Brochure 03`} />
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- Shared ------------------------------------ */

const METHOD_PILLARS = [
  {
    Icon: BookOpen,
    title: "Frameworks First",
    detail:
      "Every module opens with a battle-tested framework — not fluff. Your team learns the why before the how.",
  },
  {
    Icon: MessageSquare,
    title: "Real Scripts",
    detail:
      "60+ word-for-word scripts drawn from live deals — for greetings, discovery, objections, and closes.",
  },
  {
    Icon: Users,
    title: "60% Live Drills",
    detail:
      "The bulk of each session is role-play and live practice, not lectures. Muscle memory beats memory.",
  },
  {
    Icon: Award,
    title: "Certified & Kept",
    detail:
      "Printed certificate, LinkedIn credential, and a 40-page playbook that stays with them for life.",
  },
];

function BrandBar({ tagline, pageMark }) {
  return (
    <div className="flex items-center justify-between pb-3 border-b border-sebyx-border">
      <div className="flex items-center gap-2">
        <span className="block w-2.5 h-2.5 bg-sebyx-accent" />
        <span className="font-display text-xl font-semibold tracking-tight">
          Sebyx
        </span>
        <span className="font-mono text-[8.5px] tracking-[0.3em] uppercase text-sebyx-muted ml-2">
          / Revenue Architecture
        </span>
      </div>
      <div className="flex items-center gap-4">
        <span className="font-mono text-[8.5px] tracking-[0.3em] uppercase text-sebyx-muted">
          {tagline}
        </span>
        <span className="font-mono text-[8.5px] tracking-[0.3em] uppercase text-sebyx-accent">
          {pageMark}
        </span>
      </div>
    </div>
  );
}

function FooterStrip({ label }) {
  return (
    <div className="pt-2 mt-2 border-t border-sebyx-border flex items-center justify-between text-sebyx-muted font-mono text-[7.5px] tracking-[0.25em] uppercase">
      <span>{label}</span>
      <span>www.sebyx.com · contact@sebyx.com · +91 91084 36667</span>
    </div>
  );
}

function Contact({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <div className="w-6 h-6 border border-sebyx-border bg-sebyx-bg flex items-center justify-center shrink-0">
        <Icon size={10} className="text-sebyx-accent" />
      </div>
      <div className="leading-tight">
        <div className="font-mono text-[7px] tracking-[0.25em] uppercase text-sebyx-muted">
          {label}
        </div>
        <div className="font-display text-[10.5px] text-sebyx-text leading-tight">
          {value}
        </div>
      </div>
    </div>
  );
}
